import { assertOfflineContext } from "@/lib/offline/context";
import type { LibraryContext } from "@/native-shell/library";
import { readCompanionResponse } from "./http";
import {
  readFeedbackDetail,
  readFeedbackSnapshot,
  validFeedbackEntry,
} from "./outing-feedback";
import {
  feedbackDraftId,
  listFeedbackDrafts,
  removeFeedbackDraft,
  type FeedbackDraft,
} from "./outing-feedback-store";

export class FeedbackRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}
async function request(
  context: LibraryContext,
  url: string,
  command?: FeedbackDraft["command"],
) {
  await assertOfflineContext(context);
  if (!navigator.onLine || String(document.visibilityState) === "hidden")
    throw new Error(
      "Reconnect and open this tab to check your account. The device draft is kept.",
    );
  const response = await fetch(url, {
    method: command ? "PUT" : "GET",
    credentials: "same-origin",
    cache: "no-store",
    signal: AbortSignal.timeout(12000),
    ...(command
      ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(command),
        }
      : {}),
  });
  let value: unknown;
  try {
    value = await readCompanionResponse(response, 131072);
  } catch (error) {
    throw new FeedbackRequestError(
      error instanceof Error
        ? error.message
        : "Feedback could not be confirmed.",
      response.status,
    );
  }
  await assertOfflineContext(context);
  if (String(document.visibilityState) === "hidden")
    throw new Error(
      "Reopen this tab and check the saved version. Your draft is kept.",
    );
  return value;
}
export async function loadFeedback(context: LibraryContext) {
  const data = readFeedbackSnapshot(
    await request(context, "/api/companion/feedback"),
    context.owner,
  );
  if (!data)
    throw new Error(
      "Feedback could not be checked. Refresh before continuing.",
    );
  return data;
}
export async function loadOutingFeedback(context: LibraryContext, id: string) {
  const data = readFeedbackDetail(
    await request(context, `/api/companion/outings/${id}/feedback`),
    context.owner,
    id,
  );
  if (!data)
    throw new Error(
      "The saved answer could not be checked. Your draft is kept.",
    );
  return data;
}
export async function sendFeedbackDraft(
  context: LibraryContext,
  draft: FeedbackDraft,
) {
  const current = (await listFeedbackDrafts(context)).find(
    (d) => d.id === draft.id,
  );
  if (
    !current ||
    current.revision !== draft.revision ||
    JSON.stringify(current.command) !== JSON.stringify(draft.command)
  )
    throw new Error("This device draft changed. Reopen it before saving.");
  const c = draft.command,
    id = feedbackDraftId(c);
  const raw = await request(
    context,
    id === "settings"
      ? "/api/companion/feedback"
      : `/api/companion/outings/${id}/feedback`,
    c,
  );
  if ("outingId" in c) {
    if (
      !validFeedbackEntry(raw, context.owner) ||
      raw.outingId !== c.outingId ||
      raw.revision !== c.expectedRevision + 1 ||
      raw.sourceRevision !== c.sourceRevision ||
      raw.answer !== c.answer ||
      raw.deleted !== (c.operation === "delete") ||
      JSON.stringify(raw.reasons) !== JSON.stringify(c.reasons)
    )
      throw new Error(
        "The saved answer could not be confirmed. Retry the kept draft.",
      );
  } else {
    const receipt = readFeedbackSnapshot(raw, context.owner);
    if (
      !receipt ||
      receipt.settings.revision !== c.expectedRevision + 1 ||
      (c.operation !== "reset" &&
        receipt.settings.enabled !== (c.operation === "enable")) ||
      (c.operation === "reset" && receipt.settings.resetAt === null)
    )
      throw new Error(
        "The suggestion setting could not be confirmed. Retry the kept change.",
      );
  }
  await removeFeedbackDraft(
    context,
    id,
    draft.revision,
    draft.command.mutationId,
  );
  return raw;
}
